import {
  Meal,
  CreateMealDto,
  UpdateMealDto,
  AssignMealToDateDto,
  MealFilters,
  MealDateRangeParams,
  MealDateParams,
  MealStats
} from "../../types/meal.types";
import apiService from "../api/api.service";

class MealService {
  private static instance: MealService;

  private constructor() { }

  public static getInstance(): MealService {
    if (!MealService.instance) {
      MealService.instance = new MealService();
    }
    return MealService.instance;
  }

  // Yemek oluşturma
  public async createMeal(data: CreateMealDto): Promise<Meal> {
    try {
      const response = await apiService.post<Meal>("/meals", data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Yemek güncelleme
  public async updateMeal(id: number, data: UpdateMealDto): Promise<Meal> {
    const response = await apiService.put<Meal>(`/meals/${id}`, data);
    return response;
  }

  // Yemeği güne atama (Drag & Drop)
  public async assignMealToDate(id: number, data: AssignMealToDateDto): Promise<Meal> {
    try {
      const response = await apiService.put<Meal>(`/meals/${id}/assign-date`, data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Yemeği günden kaldırma
  public async removeMealFromDate(id: number, date: string): Promise<Meal> {
    try {
      const response = await apiService.put<Meal>(`/meals/${id}/remove-date`, { date });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ID'ye göre yemek getirme
  public async getMealById(id: number): Promise<Meal> {
    const response = await apiService.get<Meal>(`/meals/${id}`);
    return response;
  }

  // Tüm yemekleri getirme
  public async getAllMeals(branchId?: number): Promise<Meal[]> {
    try {
      const queryParams = new URLSearchParams();
      if (branchId) {
        queryParams.append('branchId', branchId.toString());
      }

      const url = queryParams.toString() ? `/meals?${queryParams.toString()}` : '/meals';

      const response = await apiService.get<Meal[]>(url);

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Belirli günün yemeklerini getirme
  public async getMealsByDate(params: MealDateParams): Promise<Meal[]> {
    try {

      // Branch ID zorunlu parametre
      if (!params.branchId) {
        throw new Error('Branch ID is required for getMealsByDate');
      }

      const queryParams = new URLSearchParams({
        date: params.date,
        branchId: params.branchId.toString(),
      });

      const url = `/meals/by-date?${queryParams.toString()}`;

      const response = await apiService.get<Meal[]>(url);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Haftalık yemekleri getirme
  public async getMealsByWeek(params: MealDateRangeParams): Promise<Meal[]> {
    try {

      // Branch ID zorunlu parametre
      if (!params.branchId) {
        throw new Error('Branch ID is required for getMealsByWeek');
      }

      const queryParams = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
        branchId: params.branchId.toString(),
      });

      const url = `/meals/by-week?${queryParams.toString()}`;

      const response = await apiService.get<Meal[]>(url);

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Yemek silme
  public async deleteMeal(id: number): Promise<void> {
    await apiService.delete(`/meals/${id}`);
  }

  // Filtrelenmiş yemek listesi
  public async getMealsWithFilters(filters: MealFilters): Promise<Meal[]> {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiService.get<Meal[]>(`/meals?${queryParams.toString()}`);
    return response;
  }

  // Yemek istatistikleri
  public async getMealStats(startDate?: string, endDate?: string): Promise<MealStats> {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await apiService.get<MealStats>(`/meals/stats?${queryParams.toString()}`);
    return response;
  }

  // Bugünkü yemekleri getirme
  public async getTodaysMeals(branchId: number): Promise<Meal[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.getMealsByDate({
      date: today,
      branchId
    });
  }

  // Bu haftaki yemekleri getirme
  public async getWeeklyMeals(branchId: number): Promise<Meal[]> {
    const today = new Date();
    const monday = new Date(today.setDate(today.getDate() - today.getDay() + 1));
    const sunday = new Date(today.setDate(today.getDate() - today.getDay() + 7));

    return this.getMealsByWeek({
      startDate: monday.toISOString().split('T')[0],
      endDate: sunday.toISOString().split('T')[0],
      branchId
    });
  }

  // Bu ayki yemekleri getirme
  public async getMonthlyMeals(branchId: number): Promise<Meal[]> {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return this.getMealsByWeek({
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0],
      branchId
    });
  }

  // Yemek durumunu güncelleme
  public async updateMealStatus(id: number, isActive: boolean): Promise<Meal> {
    return this.updateMeal(id, { isActive });
  }

  // Toplu yemek silme
  public async deleteMeals(ids: number[]): Promise<void> {
    const promises = ids.map(id => this.deleteMeal(id));
    await Promise.all(promises);
  }
}

export default MealService.getInstance();